// pages/formularios/Construtor.js — aba "Criar formulário".
//
// Out/26. Segue o design aprovado (o mesmo desenho do editor de templates:
// formulário à esquerda, prévia ao vivo à direita) com o passo "Modelo" na
// frente, como no "Criar flow" da Meta. Enquanto nenhum modelo foi
// escolhido, só aparecem os modelos e a prévia; escolher preenche telas,
// categoria e o mapeamento pro CRM, e o resto do construtor aparece.
//
// A conferência roda a cada tecla (`regras.validar`) pra o "Publicar" já
// dizer quantos itens faltam; o backend confere de novo, e o que a Meta
// recusar volta apontado para o bloco (`errosServidor`).

import { useCallback, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, ListSubheader, Menu, MenuItem, TextField, Tooltip, Typography, useMediaQuery,
} from '@mui/material';
import { FiInfo, FiPlus, FiSend, FiSmartphone, FiTrash2 } from 'react-icons/fi';
import api from '../../services/Api';
import BlocoEditor from './BlocoEditor';
import PreviaFlow from './PreviaFlow';
import SeletorModelo from './SeletorModelo';
import TesteFormulario from './TesteFormulario';
import {
  ANEXOS, CATEGORIAS, GRUPOS, LIMITE, NOME_TIPO, TIPOS,
  novoBloco, novoId, respostas, trocarTipo, validar,
} from './regras';
import {
  SYNE, ajuda, botaoPrincipal, botaoSecundario, botaoTexto, campo, dialogoPapel, menuPapel, rotuloSecao,
} from './estilo';

const VAZIO = { telas: [] };

function erroDaApi(err, padrao) {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (d?.mensagem) return d.mensagem;
  return padrao;
}

export default function Construtor({ inicial, chips, pastas, onSalvo, onPublicado, onNovo, onMensagem }) {
  const [form, setForm] = useState(() => ({
    id: inicial?.id || null,
    name: inicial?.name || '',
    device_id: inicial?.device_id || (chips.length === 1 ? chips[0].id : ''),
    category: inicial?.category || 'LEAD_GENERATION',
    model_key: inicial?.model_key || null,
    folder_id: inicial?.folder_id || '',
  }));
  const [definicao, setDefinicao] = useState(() => inicial?.definition || VAZIO);
  const [telaSel, setTelaSel] = useState(0);
  const [modeloAberto, setModeloAberto] = useState(!inicial);
  const [espiado, setEspiado] = useState(null);
  const [sujo, setSujo] = useState(false);
  const [errosServidor, setErrosServidor] = useState(() => inicial?.erros_meta || []);
  const [salvando, setSalvando] = useState(false);
  const [publicando, setPublicando] = useState(false);
  const [confirmar, setConfirmar] = useState(null);
  const [menuAdd, setMenuAdd] = useState(null);
  const [testeAberto, setTesteAberto] = useState(false);
  const raiz = useRef(null);
  const semMouse = useMediaQuery('(hover: none)');

  const temModelo = Boolean(form.model_key) || Boolean(inicial);
  const telas = definicao.telas || [];
  const tela = telas[telaSel] || telas[0];

  const mudarDef = useCallback((fn) => {
    setDefinicao((d) => fn(d));
    setSujo(true);
    setErrosServidor([]);
  }, []);
  const mudarForm = (patch) => { setForm((f) => ({ ...f, ...patch })); setSujo(true); };

  const mudarTela = (patch) => mudarDef((d) => ({ ...d, telas: d.telas.map((t, i) => (i === telaSel ? { ...t, ...patch } : t)) }));
  const mudarBlocos = (fn) => mudarDef((d) => ({ ...d, telas: d.telas.map((t, i) => (i === telaSel ? { ...t, blocos: fn(t.blocos || []) } : t)) }));

  // ── Modelo ──────────────────────────────────────────────────────────
  const aplicarModelo = (m) => {
    setDefinicao(m.definicao());
    setForm((f) => ({ ...f, model_key: m.key, category: m.categoria, name: f.name || (m.key === 'padrao' ? '' : m.nome) }));
    setTelaSel(0);
    setModeloAberto(false);
    setEspiado(null);
    setSujo(Boolean(form.id));
    setErrosServidor([]);
  };
  const escolherModelo = (m) => {
    if (temModelo && sujo && telas.length) {
      setConfirmar({
        titulo: `Trocar para “${m.nome}”?`,
        texto: 'As telas que você montou serão substituídas pelas do modelo. Nome, chip e pasta continuam.',
        rotulo: 'Trocar modelo',
        acao: () => aplicarModelo(m),
      });
      return;
    }
    aplicarModelo(m);
  };

  // ── Conferência ─────────────────────────────────────────────────────
  const problemas = useMemo(() => {
    const p = [];
    if (!form.name.trim()) p.push({ tela: null, bloco: null, campo: 'nome', mensagem: 'Dê um nome ao formulário.' });
    if (!form.device_id) p.push({ tela: null, bloco: null, campo: 'chip', mensagem: 'Escolha o chip.' });
    return [...p, ...validar(definicao), ...errosServidor];
  }, [form.name, form.device_id, definicao, errosServidor]);
  const daTela = (i) => problemas.filter((p) => p.tela === i);
  const doBloco = (id) => problemas.filter((p) => p.bloco === id);
  const daTelaSemBloco = (i, c) => problemas.filter((p) => p.tela === i && !p.bloco && p.campo === c);

  const irParaProblema = () => {
    const p = problemas[0];
    if (!p) return;
    if (p.tela != null) setTelaSel(p.tela);
    setTimeout(() => {
      const alvo = p.bloco ? raiz.current?.querySelector(`[data-bloco="${p.bloco}"]`) : raiz.current?.querySelector('[data-secao="identificacao"]');
      alvo?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 60);
  };

  // ── Salvar / publicar ───────────────────────────────────────────────
  const corpo = () => ({
    name: form.name.trim(), device_id: form.device_id, category: form.category,
    model_key: form.model_key, folder_id: form.folder_id || null, definition: definicao,
  });

  const salvar = async ({ silencioso = false } = {}) => {
    setSalvando(true);
    try {
      const res = form.id ? await api.put(`/formularios/${form.id}`, corpo()) : await api.post('/formularios', corpo());
      setForm((f) => ({ ...f, id: res.data.id }));
      setSujo(false);
      onSalvo?.(res.data);
      if (!silencioso) onMensagem({ tipo: 'success', texto: `Rascunho “${res.data.name}” salvo. Ele ainda não aparece para os clientes.` });
      return res.data;
    } catch (err) {
      onMensagem({ tipo: 'error', texto: erroDaApi(err, 'Não foi possível salvar o rascunho.') });
      return null;
    } finally {
      setSalvando(false);
    }
  };

  const publicar = async () => {
    setPublicando(true);
    try {
      let id = form.id;
      if (!id || sujo) {
        const salvo = await salvar({ silencioso: true });
        if (!salvo) return;
        id = salvo.id;
      }
      const res = await api.post(`/formularios/${id}/publicar`);
      onPublicado?.(res.data);
    } catch (err) {
      const lista = err?.response?.data?.detail?.problemas;
      if (Array.isArray(lista)) setErrosServidor(lista.filter((p) => p.mensagem?.startsWith('A Meta')));
      onMensagem({ tipo: 'error', texto: erroDaApi(err, 'Não foi possível publicar na Meta.') });
    } finally {
      setPublicando(false);
    }
  };

  // ── Telas ───────────────────────────────────────────────────────────
  const adicionarTela = () => {
    mudarDef((d) => ({ ...d, telas: [...d.telas, { id: novoId(), titulo: `Tela ${d.telas.length + 1}`, rodape: 'Continuar', blocos: [] }] }));
    setTelaSel(telas.length);
  };
  const apagarTela = () => setConfirmar({
    titulo: `Apagar a tela ${telaSel + 1}?`,
    texto: `“${tela?.titulo || 'Sem título'}” e os ${tela?.blocos?.length || 0} blocos dela saem do formulário.`,
    rotulo: 'Apagar tela', perigo: true,
    acao: () => { mudarDef((d) => ({ ...d, telas: d.telas.filter((_, i) => i !== telaSel) })); setTelaSel((i) => Math.max(0, i - 1)); },
  });
  const adicionarBloco = (tipo) => { setMenuAdd(null); mudarBlocos((bs) => [...bs, novoBloco(tipo)]); };

  // ── Prévia ──────────────────────────────────────────────────────────
  // Uma definição por modelo espiado, não uma por render (cada chamada
  // gera ids novos e remontaria a prévia a cada tecla).
  const amostra = useMemo(() => (espiado ? espiado.definicao() : null), [espiado]);
  const previa = amostra || definicao;
  const previaIndice = espiado ? 0 : telaSel;
  const listaRespostas = respostas(definicao);
  const ehUltima = telaSel === telas.length - 1;
  const nProblemas = problemas.length;

  return (
    <Box ref={raiz} sx={{ display: 'grid', gap: { xs: 3, md: 4 }, gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 7fr) minmax(0, 5fr)' }, alignItems: 'start' }}>
      <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
        {form.id && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '0.84rem', color: 'var(--fm-body)' }}>
              Editando o rascunho <b style={{ color: 'var(--fm-title)' }}>“{form.name || 'Sem nome'}”</b>
            </Typography>
            <Button size="small" onClick={onNovo} sx={botaoTexto}>Começar outro formulário</Button>
          </Box>
        )}

        <SeletorModelo
          modelo={form.model_key}
          aberto={modeloAberto || !temModelo}
          onEscolher={escolherModelo}
          onAbrir={() => setModeloAberto(true)}
          onEspiar={setEspiado}
          definicaoAtual={definicao}
        />
        {modeloAberto && temModelo && (
          <Box sx={{ mt: -1.5 }}>
            <Button size="small" onClick={() => { setModeloAberto(false); setEspiado(null); }} sx={botaoTexto}>Manter o modelo atual</Button>
          </Box>
        )}

        {temModelo && (
          <>
            {/* ── Identificação ── */}
            <Box data-secao="identificacao">
              <Typography sx={{ ...rotuloSecao, mb: 1.5 }}>Identificação</Typography>
              {!chips.length && (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                  Nenhum chip com conta do WhatsApp configurada. Conecte um em Administrativo → Chips.
                </Alert>
              )}
              <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                <TextField
                  size="small" label="Nome do formulário" value={form.name}
                  onChange={(e) => mudarForm({ name: e.target.value })} inputProps={{ maxLength: 200 }}
                  error={problemas.some((p) => p.campo === 'nome')}
                  helperText="Só a sua equipe vê este nome." sx={campo} fullWidth
                />
                <TextField
                  select size="small" label="Chip (WABA)" value={form.device_id}
                  onChange={(e) => mudarForm({ device_id: e.target.value })}
                  SelectProps={{ MenuProps: menuPapel }} error={problemas.some((p) => p.campo === 'chip')}
                  helperText="O formulário será criado na conta deste chip." sx={campo} fullWidth
                >
                  {chips.map((c) => <MenuItem key={c.id} value={c.id}>{c.name} — {c.phone_number}</MenuItem>)}
                </TextField>
                <TextField
                  select size="small" label="Categoria" value={form.category}
                  onChange={(e) => mudarForm({ category: e.target.value })}
                  SelectProps={{ MenuProps: menuPapel }} helperText="Exigida pela Meta para classificar o formulário." sx={campo} fullWidth
                >
                  {CATEGORIAS.map((c) => <MenuItem key={c.v} value={c.v}>{c.l}</MenuItem>)}
                </TextField>
                <TextField
                  select size="small" label="Pasta no CRM" value={form.folder_id}
                  onChange={(e) => mudarForm({ folder_id: e.target.value })}
                  SelectProps={{ MenuProps: menuPapel, displayEmpty: true }} InputLabelProps={{ shrink: true }}
                  helperText="Quem responder entra nesta pasta de Contatos." sx={campo} fullWidth
                >
                  <MenuItem value=""><em>Nenhuma pasta</em></MenuItem>
                  {pastas.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                </TextField>
              </Box>
            </Box>

            <Box sx={{ height: '1px', bgcolor: 'var(--fm-div)' }} />

            {/* ── Telas ── */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={rotuloSecao}>Telas <Box component="span" sx={{ color: 'var(--fm-red)' }}>*</Box></Typography>
                  <Typography sx={ajuda}>Cada tela é uma etapa. O cliente avança pelo botão do rodapé.</Typography>
                </Box>
                <Button size="small" startIcon={<FiPlus size={14} />} onClick={adicionarTela} disabled={telas.length >= LIMITE.telas} sx={botaoTexto}>
                  Adicionar tela
                </Button>
              </Box>

              <Box role="tablist" aria-label="Telas do formulário" sx={{ display: 'flex', flexWrap: 'wrap', mb: 2, border: '1px solid var(--fm-line)', borderRadius: '8px', overflow: 'hidden', width: 'fit-content', maxWidth: '100%' }}>
                {telas.map((t, i) => {
                  const ativa = i === telaSel;
                  const erro = daTela(i).length > 0;
                  return (
                    <Box
                      key={t.id} component="button" type="button" role="tab" aria-selected={ativa}
                      onClick={() => setTelaSel(i)}
                      sx={{
                        border: 'none', borderRight: i < telas.length - 1 ? '1px solid var(--fm-line)' : 'none',
                        px: 1.75, py: 0.9, font: 'inherit', fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.8,
                        bgcolor: ativa ? 'var(--fm-sel)' : 'var(--fm-paper)', color: ativa ? 'var(--fm-ink)' : 'var(--fm-ink2)', fontWeight: ativa ? 700 : 500,
                        maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        '&:focus-visible': { outline: '2px solid var(--fm-focus)', outlineOffset: -2 },
                      }}
                    >
                      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{i + 1} · {(t.titulo || '').trim() || 'Sem título'}</Box>
                      {erro && <Box component="span" aria-label="tem ajustes" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'var(--fm-red)', flexShrink: 0 }} />}
                    </Box>
                  );
                })}
              </Box>

              {tela && (
                <Box sx={{ border: '1px solid var(--fm-bdr)', borderRadius: '12px', p: { xs: 1.75, sm: 2.25 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <TextField
                      size="small" label={`Título da tela ${telaSel + 1}`} value={tela.titulo || ''}
                      onChange={(e) => mudarTela({ titulo: e.target.value })}
                      error={daTelaSemBloco(telaSel, 'titulo').length > 0}
                      helperText={daTelaSemBloco(telaSel, 'titulo')[0]?.mensagem || 'Aparece no topo da tela, no WhatsApp do cliente.'}
                      sx={campo} fullWidth
                    />
                    {telas.length > 1 && (
                      <Tooltip title="Apagar tela">
                        <IconButton aria-label={`Apagar a tela ${telaSel + 1}`} onClick={apagarTela} sx={{ color: 'var(--fm-red)', mt: 0.25 }}>
                          <FiTrash2 size={16} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>

                  <Box>
                    <Typography sx={{ ...rotuloSecao, mb: 1 }}>Conteúdo da tela</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {(tela.blocos || []).map((b, k) => (
                        <BlocoEditor
                          key={b.id} bloco={b} numero={k + 1}
                          primeiro={k === 0} ultimo={k === tela.blocos.length - 1}
                          problemas={doBloco(b.id)}
                          onChange={(novo) => mudarBlocos((bs) => bs.map((x) => (x.id === b.id ? novo : x)))}
                          onTrocarTipo={(tipo) => mudarBlocos((bs) => bs.map((x) => (x.id === b.id ? trocarTipo(x, tipo) : x)))}
                          onMover={(dir) => mudarBlocos((bs) => {
                            const j = k + dir; if (j < 0 || j >= bs.length) return bs;
                            const n = [...bs]; [n[k], n[j]] = [n[j], n[k]]; return n;
                          })}
                          onApagar={() => mudarBlocos((bs) => bs.filter((x) => x.id !== b.id))}
                        />
                      ))}
                      {!(tela.blocos || []).length && (
                        <Typography sx={{ ...ajuda, py: 1 }}>Esta tela ainda está vazia. Adicione uma pergunta, uma escolha ou um texto.</Typography>
                      )}
                    </Box>
                    <Button
                      size="small" startIcon={<FiPlus size={14} />} sx={{ ...botaoTexto, mt: 1 }}
                      onClick={(e) => setMenuAdd(e.currentTarget)} disabled={(tela.blocos || []).length >= LIMITE.blocos}
                      aria-haspopup="menu"
                    >
                      Adicionar à tela
                    </Button>
                    <Menu anchorEl={menuAdd} open={Boolean(menuAdd)} onClose={() => setMenuAdd(null)} {...menuPapel}>
                      {GRUPOS.flatMap((g) => [
                        <ListSubheader key={`g-${g}`}>{g}</ListSubheader>,
                        ...TIPOS.filter((t) => t.grupo === g).map((t) => (
                          <MenuItem key={t.v} onClick={() => adicionarBloco(t.v)} sx={{ fontSize: '0.86rem' }}>
                            {t.l}
                            {ANEXOS.includes(t.v) && !ehUltima && <Box component="span" sx={{ ml: 1, fontSize: '0.7rem', color: 'var(--fm-cap)' }}>só na última tela</Box>}
                          </MenuItem>
                        )),
                      ])}
                    </Menu>
                    {daTela(telaSel).filter((p) => !p.bloco && !p.campo).map((p, k) => (
                      <Typography key={k} role="alert" sx={{ fontSize: '0.76rem', color: 'var(--fm-red)', fontWeight: 600, mt: 0.75 }}>{p.mensagem}</Typography>
                    ))}
                  </Box>

                  <TextField
                    size="small" label="Botão do rodapé" value={tela.rodape || ''}
                    onChange={(e) => mudarTela({ rodape: e.target.value })}
                    error={daTelaSemBloco(telaSel, 'rodape').length > 0}
                    helperText={daTelaSemBloco(telaSel, 'rodape')[0]?.mensagem || (
                      <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                        <span>{ehUltima ? 'Envia as respostas.' : 'Leva o cliente para a próxima tela.'}</span>
                        <span>{(tela.rodape || '').trim().length}/{LIMITE.rodape}</span>
                      </Box>
                    )}
                    sx={campo} fullWidth
                  />
                </Box>
              )}
            </Box>

            <Box sx={{ height: '1px', bgcolor: 'var(--fm-div)' }} />

            {/* ── Respostas no CRM ── */}
            <Box>
              <Typography sx={rotuloSecao}>Respostas no CRM</Typography>
              <Typography sx={{ ...ajuda, mb: 1.5 }}>O contato é encontrado pelo número do WhatsApp, então não é preciso perguntar o telefone.</Typography>
              {!listaRespostas.length && <Typography sx={ajuda}>Este formulário ainda não tem perguntas.</Typography>}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {listaRespostas.map(({ tela: i, bloco: b }) => {
                  const anexo = ANEXOS.includes(b.tipo);
                  const destino = b.destino || 'campo';
                  const erro = doBloco(b.id).find((p) => p.campo === 'destino');
                  const dica = anexo ? 'O arquivo fica junto da resposta do formulário.'
                    : destino === 'nome' ? 'Preenche o nome só se o contato ainda não tiver um.'
                      : destino === 'campo' ? `Campo “${(b.rotulo || '').trim() || 'sem rótulo'}” do contato. Criado se ainda não existir.`
                        : 'Fica só na resposta do formulário.';
                  return (
                    <Box key={b.id} sx={{ border: '1px solid var(--fm-bdr)', borderRadius: '10px', p: 1.75, display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, alignItems: 'start' }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: 'var(--fm-title)', wordBreak: 'break-word' }}>{(b.rotulo || '').trim() || 'Sem rótulo'}</Typography>
                        <Typography sx={ajuda}>Tela {i + 1} · {NOME_TIPO[b.tipo]}</Typography>
                      </Box>
                      {anexo ? (
                        <Typography sx={{ ...ajuda, pt: 0.5 }}>{dica}</Typography>
                      ) : (
                        <TextField
                          select size="small" label="Vai para" value={destino}
                          onChange={(e) => mudarDef((d) => ({ ...d, telas: d.telas.map((t) => ({ ...t, blocos: (t.blocos || []).map((x) => (x.id === b.id ? { ...x, destino: e.target.value } : x)) })) }))}
                          SelectProps={{ MenuProps: menuPapel }} error={Boolean(erro)} helperText={erro?.mensagem || dica}
                          sx={campo} fullWidth
                        >
                          {b.tipo === 'texto' && <MenuItem value="nome">Nome do contato</MenuItem>}
                          <MenuItem value="campo">Campo personalizado</MenuItem>
                          <MenuItem value="nenhum">Não salvar no CRM</MenuItem>
                        </TextField>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Alert
              severity="info" icon={<FiInfo size={18} />}
              sx={{ borderRadius: 2, bgcolor: 'var(--fm-info-bg)', color: 'var(--fm-info-t)', border: '1px solid var(--fm-info-b)', '& .MuiAlert-icon': { color: 'var(--fm-info-i)' } }}
            >
              O rascunho pode ser editado à vontade. Depois de <b>publicado</b>, a Meta não permite mais alterar o formulário:
              para mudar, duplique-o em <b>Ver e editar formulários</b> e publique a cópia.
            </Alert>

            {nProblemas > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: -1 }}>
                <Typography sx={{ fontSize: '0.82rem', color: 'var(--fm-red)', fontWeight: 600 }}>
                  {nProblemas === 1 ? '1 item precisa de atenção antes de publicar.' : `${nProblemas} itens precisam de atenção antes de publicar.`}
                </Typography>
                <Button size="small" onClick={irParaProblema} sx={botaoTexto}>Ir para o primeiro</Button>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button variant="outlined" onClick={() => salvar()} disabled={salvando || publicando || !form.device_id || !form.name.trim()} sx={{ ...botaoSecundario, px: 2.5, py: 1.1 }}>
                {salvando && !publicando ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : 'Salvar rascunho'}
              </Button>
              {/* PR 2: ver no celular antes de publicar (publicado não muda). */}
              <Tooltip title={nProblemas > 0 ? 'Ajuste os itens marcados antes de testar.' : 'Manda o rascunho para o seu celular. Nada vai para o CRM.'}>
                <span>
                  <Button
                    variant="outlined" onClick={() => setTesteAberto(true)} disabled={nProblemas > 0 || salvando || publicando}
                    startIcon={<FiSmartphone size={15} />} sx={{ ...botaoSecundario, px: 2.5, py: 1.1 }}
                  >
                    Enviar teste
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="contained" disableElevation onClick={publicar} disabled={nProblemas > 0 || publicando || salvando}
                startIcon={publicando ? <CircularProgress size={15} sx={{ color: 'inherit' }} /> : <FiSend size={15} />}
                sx={{ ...botaoPrincipal, flex: 1, minWidth: 200, py: 1.1 }}
              >
                {publicando ? 'Publicando na Meta…' : 'Publicar na Meta'}
              </Button>
            </Box>
          </>
        )}
      </Box>

      {/* ── Prévia ── */}
      <Box sx={{ minWidth: 0, position: { md: 'sticky' }, top: { md: 16 } }}>
        <Typography sx={{ ...rotuloSecao, mb: 1.25 }}>
          {espiado ? `Prévia do modelo · ${espiado.nome}` : 'Como o cliente vai ver'}
        </Typography>
        {previa.telas?.length ? (
          <PreviaFlow definicao={previa} indice={previaIndice} onTrocar={espiado ? undefined : setTelaSel} />
        ) : (
          <Box sx={{ border: '1px dashed var(--fm-bdr-h)', borderRadius: '16px', p: 4, textAlign: 'center' }}>
            <Typography sx={{ fontFamily: SYNE, fontWeight: 700, color: 'var(--fm-title)', mb: 0.5 }}>Escolha um modelo</Typography>
            <Typography sx={ajuda}>{semMouse ? 'Toque num modelo para começar a montar.' : 'Passe o mouse num modelo para ver as telas dele aqui.'}</Typography>
          </Box>
        )}
      </Box>

      <TesteFormulario
        open={testeAberto}
        onClose={() => setTesteAberto(false)}
        formId={form.id}
        nome={form.name.trim()}
        chipNome={chips.find((c) => c.id === form.device_id)?.name}
        salvar={async () => ((form.id && !sujo) ? { id: form.id } : salvar({ silencioso: true }))}
        onProblemas={(lista) => {
          setErrosServidor(lista.filter((p) => p.mensagem?.startsWith('A Meta')));
          onMensagem({ tipo: 'error', texto: 'A Meta recusou o formulário. Ajuste os itens marcados.' });
        }}
      />

      <Dialog open={Boolean(confirmar)} onClose={() => setConfirmar(null)} maxWidth="xs" fullWidth PaperProps={dialogoPapel}>
        <DialogTitle sx={{ fontFamily: SYNE, fontWeight: 800 }}>{confirmar?.titulo}</DialogTitle>
        <DialogContent><Typography sx={{ fontSize: '0.9rem', color: 'var(--fm-body)' }}>{confirmar?.texto}</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmar(null)} sx={{ ...botaoTexto, color: 'var(--fm-body)' }}>Cancelar</Button>
          <Button
            variant="contained" disableElevation
            onClick={() => { confirmar.acao(); setConfirmar(null); }}
            sx={confirmar?.perigo ? { ...botaoPrincipal, bgcolor: 'var(--fm-red)', color: '#fff', boxShadow: 'none', '&:hover': { bgcolor: 'var(--fm-red)', filter: 'brightness(0.92)' } } : botaoPrincipal}
          >
            {confirmar?.rotulo}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
